// src/pages/vendors/purchase-order-form-components/SupplierSelectionSection.jsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  alpha,
  useTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Search as SearchIcon,
  AccountBox as AccountBoxIcon
} from '@mui/icons-material';

const SupplierSelectionSection = ({ purchaseOrder, suppliers, onPOChange, canEdit = true }) => {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const selectedSupplier = suppliers.find(s => s.id === purchaseOrder.supplier_id);

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectSupplier = (supplier) => {
    onPOChange('supplier_id', supplier.id);
    setDialogOpen(false);
    setSearchTerm('');
  };

  return (
    <>
      <Paper sx={{ 
        p: 3, 
        mb: 3, 
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
      }}>
        <Typography variant="h6" sx={{ 
          mb: 3, 
          fontWeight: 500, 
          fontSize: '1.2rem',
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <PersonIcon sx={{ fontSize: 28 }} />
          Vendor Information
        </Typography>

        <Button
          fullWidth
          variant="outlined"
          onClick={() => canEdit && setDialogOpen(true)}
          disabled={!canEdit}
          sx={{
            height: 56,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            justifyContent: 'flex-start',
            px: 3,
            py: 2.5,
            '&:hover': {
              border: canEdit ? `1px solid ${theme.palette.primary.main}` : undefined,
              boxShadow: canEdit ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}` : undefined
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <AccountBoxIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <Box sx={{ textAlign: 'left', flex: 1 }}>
              {selectedSupplier ? (
                <>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                    {selectedSupplier.name}
                  </Typography>
                  {selectedSupplier.email && (
                    <Typography variant="caption" color="text.secondary">
                      {selectedSupplier.email}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                  Select Vendor
                </Typography>
              )}
            </Box>
          </Box>
        </Button>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSearchTerm('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 500, fontSize: '1.2rem' }}>
          Select Vendor
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2,
                mb: 2
              }
            }}
          />
          <List>
            {filteredSuppliers.length === 0 ? (
              <ListItem>
                <ListItemText primary="No vendors found" />
              </ListItem>
            ) : (
              filteredSuppliers.map((supplier) => (
                <ListItem key={supplier.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleSelectSupplier(supplier)}
                    selected={purchaseOrder.supplier_id === supplier.id}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.15)
                        }
                      }
                    }}
                  >
                    <ListItemText
                      primary={supplier.name}
                      secondary={supplier.email}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setSearchTerm('');
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SupplierSelectionSection;